import React from "react";
import { useParams } from "react-router-dom";
import { Button, Chip, Stack, Typography } from "@mui/material";

import {
  ContentLayout,
  HeaderBreadcrumbs,
  DeleteModal,
  Loader,
  CustomeDialog,
} from "../../../shared";

import { useStampCard } from "../hooks";
import {
  StampCardGeneralDetails,
  StampCardProductsList,
  StampCardsRewardsList,
  StampUsageHistory,
  StampCardCustomersList,
  StampCardCouponReport,
} from "../components";

const statusStyles = {
  published: { backgroundColor: "#E1FBF2", color: "#0F875B" },
  draft: { backgroundColor: "#1560D414", color: "#1560D4" },
  paused: { backgroundColor: "#FFF6E0", color: "#B26A00" },
  ended: { backgroundColor: "#FFE7E3", color: "#C42B1C" },
  other: { backgroundColor: "#F0F0F0", color: "#000000" }, // default style
};

export const StampCardsDetails = () => {
  const { id } = useParams();
  const {
    state,
    toggleModal,
    handleDeleteStampCard,
    isEditable,
    globalData,
    handleModal,
    handleChangeStampStatus,
  } = useStampCard();

  const { isLoading } = state;

  const breadcrumbs = [
    { name: "Dashboard", href: "/" },
    { name: "Stamp Cards", href: "/stamp-cards" },
    { name: state?.data?.name || "Loading..." },
  ];
  const status = state?.data?.status;
  const isPublished = status === 2;
  const isPaused = status === 3;
  const isDraft = status === 1;
  const isLocked = isPublished || isPaused;

  const tabs = React.useMemo(
    () => [
      {
        label: "Details",
        value: "1",
        panel: isLoading ? (
          <Loader />
        ) : (
          <StampCardGeneralDetails
            state={state}
            isEditable={isEditable}
            id={id}
            isPublished={isLocked}
          />
        ),
      },
      {
        label: "Eligible Products",
        value: "2",
        panel: <StampCardProductsList isPublished={isLocked} />,
      },
      {
        label: "Stamp Reward Mapping",
        value: "3",
        panel: <StampCardsRewardsList isPublished={isLocked} />,
      },
      {
        label: "Usage History",
        value: "4",
        panel: <StampUsageHistory isPublished={isLocked} />,
      },
      {
        label: "Coupon Report",
        value: "5",
        panel: <StampCardCouponReport isDraft={isDraft} />,
      },
      {
        label: "Customers",
        value: "6",
        panel: <StampCardCustomersList isPublished={isLocked} />,
      },
    ],
    [isLoading, state, isEditable, id, isDraft],
  );

  const statusObj = globalData?.stamp_cards?.stamp_card_status?.find(
    (item) => item.value === state?.data?.status,
  );

  const label = statusObj?.label || "Other";
  const styleKey = label.toLowerCase();
  const chipStyle = statusStyles[styleKey] || statusStyles.other;

  return (
    <ContentLayout
      title={state?.data?.name || ""}
      chipType="other"
      statusContent={
        <Chip
          label={label}
          size="small"
          sx={{
            color: chipStyle.color,
            fontWeight: 500,
            backgroundColor: chipStyle.backgroundColor,
          }}
        />
      }
      breadcrumbs={<HeaderBreadcrumbs links={breadcrumbs} />}
      tabProps={{
        label: "stampCardDetails",
        list: tabs,
      }}
      rightContent={
        <>
          {(isPublished || isPaused) && (
            <Button
              type="button"
              size="medium"
              variant="text"
              onClick={() => {
                if (isPublished) {
                  handleModal("unpublish", true);
                } else if (isPaused) {
                  handleModal("publish", true);
                }
              }}
              sx={{ minWidth: "40px", p: "8px" }}
            >
              <img
                alt="status"
                className="status-icon"
                src={isPublished ? "/icons/ic_pause.svg" : "/icons/ic_play.svg"}
              />
            </Button>
          )}

          {(isDraft || isPaused) && (
            <Button
              type="button"
              size="medium"
              variant="text"
              onClick={() => handleModal("delete", true)}
              sx={{ minWidth: "40px", p: "8px" }}
            >
              <img src="/icons/ic_delete_blue.svg" />
            </Button>
          )}

          {isDraft && (
            <Button
              type="button"
              size="medium"
              variant="contained"
              onClick={() => handleModal("publish", true)}
              sx={{ px: "16px" }}
            >
              Publish
            </Button>
          )}
        </>
      }
    >
      {/* Delete Modal */}
      <DeleteModal
        open={state.deleteModalOpen}
        handleClose={() => handleModal("delete", false)}
        onConfirm={() => handleDeleteStampCard("details")}
      />
      <CustomeDialog
        open={state.publishModalOpen}
        // handleClose={() => handleModal("publish", false)}
        PaperProps={{
          sx: {
            width: "423px",
            height: "167px",
            p: "24px 24px 16px 24px",
            gap: "12px",
          },
        }}
        title="Publish Stamp card"
        content={
          <Stack flexDirection="column" justifyContent="space-between">
            <Typography fontSize="14px" fontWeight={400} lineHeight="16px">
              Are you sure you want to publish this stamp card? Once published,
              it will be visible and available for customers to use.
            </Typography>
            <Stack
              flexDirection="row"
              gap="12px"
              justifyContent="end"
              mt="16px"
            >
              <Button
                size="small"
                variant="text"
                color="info"
                onClick={() => handleModal("publish", false)}
              >
                No
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => handleChangeStampStatus(2)}
              >
                Yes
              </Button>
            </Stack>
          </Stack>
        }
      />
      <CustomeDialog
        open={state.unpublishModalOpen}
        PaperProps={{
          sx: {
            width: "423px",
            height: "167px",
            p: "24px 24px 16px 24px",
            gap: "12px",
          },
        }}
        title="Unpublish Stamp card"
        content={
          <Stack flexDirection="column" justifyContent="space-between">
            <Typography fontSize="14px" fontWeight={400} lineHeight="16px">
              Are you sure you want to unpublish this stamp card? It will no
              longer be available to customers.
            </Typography>
            <Stack
              flexDirection="row"
              gap="12px"
              justifyContent="end"
              mt="16px"
            >
              <Button
                size="small"
                variant="text"
                color="info"
                onClick={() => handleModal("unpublish", false)}
              >
                No
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => handleChangeStampStatus(3)}
              >
                Yes
              </Button>
            </Stack>
          </Stack>
        }
      />
    </ContentLayout>
  );
};
